import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../context/UnifiedAuthContext.jsx';
import { isPortalFlowId, portalDashboardPath, watchPortalLogin } from '../../utils/portalLogin.js';
import './Login.scss';

export default function PortalLoginComplete() {
    const route = useLocation();
    const { technician, loading, urlParams } = useAuth();
    const params = new URLSearchParams(route.search);
    const flowId = params.get('portalFlow');
    const locationId = params.get('location');
    const userId = params.get('user');
    const [returnState, setReturnState] = useState('waiting');

    useEffect(() => {
        if (
            window.self !== window.top
            || loading
            || !technician
            || !isPortalFlowId(flowId)
            || locationId !== urlParams.location
            || userId !== urlParams.user
        ) return;

        // If the original tab was closed or rejects third-party cookies, keep
        // this authenticated tab usable instead of closing it on a blind timer.
        const fallbackTimer = setTimeout(() => setReturnState('fallback'), 12000);
        const stop = watchPortalLogin({
            flowId,
            onTimeout: () => {
                clearTimeout(fallbackTimer);
                setReturnState('expired');
            },
            onStatus: (result) => {
                if (result.status === 'expired') {
                    clearTimeout(fallbackTimer);
                    setReturnState('expired');
                    return true;
                }
                if (
                    result.status !== 'resumed'
                    || result.locationId !== locationId
                    || result.userId !== userId
                ) return false;
                clearTimeout(fallbackTimer);
                setReturnState('returned');
                // Browsers normally return focus to the portal when this
                // script-opened tab closes. No opener access is necessary.
                window.close();
                return true;
            }
        });
        return () => {
            clearTimeout(fallbackTimer);
            stop();
        };
    }, [flowId, loading, locationId, technician, urlParams.location, urlParams.user, userId]);

    const message = returnState === 'returned'
        ? 'Elevate is ready in your portal. You can close this tab if it remains open.'
        : returnState === 'expired'
            ? 'You are signed in, but this return link has expired. Continue in this tab, or start sign-in again from your portal.'
            : returnState === 'fallback'
                ? 'You are signed in. Return to your original portal tab to finish. If it shows Continue in portal, select it. This tab will close when Elevate is ready.'
                : 'Refreshing Elevate in your portal. This tab will close when it is ready.';

    return (
        <div className="login">
            <div className="login__container">
                <div className="login__card">
                    <div className="login__brand">
                        <div className="login__brand-mark"><ShieldCheck size={25} /></div>
                        <span>Ejento Elevate</span>
                    </div>
                    <div className="login__header">
                        <h1>Signed in</h1>
                    </div>
                    <div className="login__confirmation" role="status">
                        <CheckCircle size={24} />
                        <p>{message}</p>
                    </div>
                    <Link
                        className="login__button login__return-link"
                        to={portalDashboardPath({ locationId, userId })}
                        replace
                    >
                        Continue in this tab
                    </Link>
                </div>
            </div>
        </div>
    );
}
